import { Circle, Center, Image } from 'native-base'
import React, { useMemo, useEffect } from 'react'
import Animated, {
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'

import { Images } from '~images'
import { LoadingStateImageTypes } from '~types/LoadingStateImage'
const AnimatedCircle = Animated.createAnimatedComponent(Circle)

export const LoadingStateImage = ({
  showLoadingState = true,
  type = 'logo',
  size = 50,
  velocity = 5000,
}: LoadingStateImageTypes) => {
  const animatedValue = useSharedValue(showLoadingState ? 0 : 1)

  const imageSize = useMemo(() => `${size}px`, [size])

  const circleStyleProps = useMemo(
    () =>
      ({
        borderColor: 'logoBlue',
        borderWidth: '1px',
        position: 'absolute',
        size: `${size}px`,
      } as const),
    [size]
  )

  const valueInterpolation = useMemo(() => [0, 0.33, 0.66, 1], [])
  const sizeInterpolation = useMemo(
    () => ({
      circle1: [size - 1, size * 1.75, size * 2.25, size * 2.25],
      circle2: [size - 2, size - 2, size * 1.7, size * 2.2],
    }),
    [size]
  )
  const opacityInterpolation = useMemo(
    () => ({
      circle1: type === 'logo' ? [0, 0.4, 0.2, 0] : [0, 0.8, 0.6, 0],
      circle2: type === 'logo' ? [0, 0.4, 0.3, 0] : [0, 0.6, 0.8, 0],
    }),
    [type]
  )
  const animatedCircle1Style = useAnimatedStyle(
    () => ({
      width: interpolate(animatedValue.value, valueInterpolation, sizeInterpolation.circle1),
      height: interpolate(animatedValue.value, valueInterpolation, sizeInterpolation.circle1),
      opacity: interpolate(animatedValue.value, valueInterpolation, opacityInterpolation.circle1),
    }),
    [animatedValue]
  )

  const animatedCircle2Style = useAnimatedStyle(
    () => ({
      width: interpolate(animatedValue.value, valueInterpolation, sizeInterpolation.circle2),
      height: interpolate(animatedValue.value, valueInterpolation, sizeInterpolation.circle2),
      opacity: interpolate(animatedValue.value, valueInterpolation, opacityInterpolation.circle2),
    }),
    [animatedValue]
  )
  useEffect(() => {
    if (showLoadingState) {
      animatedValue.value = 0
      animatedValue.value = withRepeat(withTiming(1, { duration: velocity }), -1, false)
    } else {
      cancelAnimation(animatedValue)
      animatedValue.value = withTiming(1, { duration: 500 })
    }
  }, [showLoadingState, animatedValue, velocity])

  const logoImage = useMemo(
    () => ({
      placeholder: (
        <Circle size={imageSize} bg="offBlue" p="2">
          <Image
            alt="logo"
            h="100%"
            ignoreFallback
            resizeMode="contain"
            source={Images.ROCKETFUEL_CIRCULAR}
            w="100%"
          />
        </Circle>
      ),
      logo: (
        <Image
          alt="logo"
          resizeMode="cover"
          h={imageSize}
          w={imageSize}
          source={Images.ROCKETFUEL_CIRCULAR}
        />
      ),
    }),
    [imageSize]
  )

  return (
    <Center>
      <AnimatedCircle {...circleStyleProps} style={animatedCircle1Style} />
      <AnimatedCircle {...circleStyleProps} style={animatedCircle2Style} />
      {logoImage[type]}
    </Center>
  )
}
